// "Report di calcolo" card: version (full / compact), on-screen paged preview of the PDF report the server renders
// (refreshed automatically while open), its print and the DXF export of the construction detail. The preview and the
// print ask for the language first (ReportLanguageMenu).
import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { Accordion } from "../../../components/ds2/Accordion";
import { Button } from "../../../components/ds2/Button";
import useI18n from "../../../hooks/useTranslations";
import type { LanguageCode } from "../../../types";
import { useCollapsed } from "./useCollapsed";
import { exportDxf as downloadDxf, dxfAvailability } from "./dxf";
import styles from "./ReportCard.module.css";
import type { ReportVersion, ReportViews } from "./reportPayload";
import ReportLanguageMenu from "./ReportLanguageMenu";
import ReportPreview from "./ReportPreview";
import { resolveReportViews, useLatest, useReportPdf, type ReportSource } from "./useReportPdf";

export type ReportCardHandle = {
  // on-screen preview (guided tutorial): opens it, expanding the card if needed, and resolves once built
  openPreview: () => Promise<void>;
  closePreview: () => void;
  isPreviewOpen: () => boolean;
};

export type ReportCardProps = ReportSource & {
  // controlled version (optional): lets the page share it with other report buttons
  version?: ReportVersion;
  onVersionChange?: (version: ReportVersion) => void;
  ref?: Ref<ReportCardHandle>;
};

// delay of the automatic preview refresh after the last change
const REFRESH_MS = 700;

const ReportCard: FC<ReportCardProps> = ({ version: versionProp, onVersionChange, ref, ...source }) => {
  const { state, blockReason, ctx } = source;
  const t = useI18n().translation.screwConnectorModule.calculation.report;
  const [collapsed, toggle] = useCollapsed("collapsed_reportCard");
  const [localVersion, setLocalVersion] = useState<ReportVersion>("compact");
  const version = versionProp ?? localVersion;
  const radioName = useId();
  const { print, isPrinting, preview } = useReportPdf(source);
  const dxf = dxfAvailability(state, blockReason);

  const [open, setOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const urlRef = useRef<string | null>(null);

  const src = useLatest(source);
  const versionRef = useLatest(version);
  const openRef = useLatest(open);
  const views = useRef<ReportViews>({});
  // the preview's language, picked on opening it (the user's, opened by the tutorial)
  const previewLanguage = useRef<LanguageCode | undefined>(undefined);
  const refresh = useRef({ timer: 0, busy: false, dirty: false });

  // the object URL on screen: the previous one is released
  const showPdf = useCallback((url: string | null) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setPreviewUrl(url);
  }, []);
  useEffect(() => () => showPdf(null), [showPdf]);

  // asks the server for the preview PDF again; withImages: capture the 3D views again first
  const rebuild = useCallback(
    async (withImages: boolean) => {
      if (withImages) views.current = await resolveReportViews(src.current);
      try {
        const url = await preview(versionRef.current, views.current, previewLanguage.current);
        // the preview was closed meanwhile
        if (!openRef.current) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        showPdf(url);
        setPreviewFailed(false);
      } catch {
        setPreviewFailed(true);
      }
    },
    [src, versionRef, openRef, preview, showPdf],
  );

  // automatic refresh while the preview is open: REFRESH_MS after the last change; changes during a rebuild schedule
  // a new one
  const scheduleRefresh = useCallback(() => {
    const r = refresh.current;
    if (!openRef.current) return;
    if (r.busy) {
      r.dirty = true;
      return;
    }
    window.clearTimeout(r.timer);
    r.timer = window.setTimeout(async () => {
      if (!openRef.current || r.busy) return;
      r.busy = true;
      r.dirty = false;
      try {
        await rebuild(true);
      } finally {
        r.busy = false;
        if (r.dirty) scheduleRefresh();
      }
    }, REFRESH_MS);
  }, [openRef, rebuild]);

  // the context is rebuilt by the page on every render: compare it by value
  const ctxKey = useMemo(() => JSON.stringify(ctx), [ctx]);
  useEffect(() => {
    scheduleRefresh();
  }, [state, ctxKey, blockReason, scheduleRefresh]);

  useEffect(() => {
    const r = refresh.current;
    return () => window.clearTimeout(r.timer);
  }, []);

  const setVersion = (v: ReportVersion) => {
    setLocalVersion(v);
    onVersionChange?.(v);
  };

  // a new version only asks for the document again (same views)
  useEffect(() => {
    if (openRef.current) void rebuild(false);
  }, [version, openRef, rebuild]);

  const closePreview = useCallback(() => {
    setOpen(false);
    showPdf(null);
  }, [showPdf]);

  const openPreview = async (language?: LanguageCode) => {
    previewLanguage.current = language;
    const r = refresh.current;
    window.clearTimeout(r.timer);
    r.busy = true;
    r.dirty = false;
    setOpening(true);
    setPreviewFailed(false);
    setOpen(true);
    openRef.current = true;
    try {
      await rebuild(true);
    } finally {
      r.busy = false;
      setOpening(false);
      if (r.dirty) scheduleRefresh();
    }
  };

  const doExportDxf = useCallback(() => {
    const s = src.current;
    downloadDxf(s.state, s.ctx, s.blockReason);
  }, [src]);

  const collapsedRef = useLatest(collapsed);
  const openPreviewRef = useLatest(openPreview);
  useImperativeHandle(
    ref,
    () => ({
      openPreview: async () => {
        if (collapsedRef.current) toggle();
        if (!openRef.current) await openPreviewRef.current();
      },
      closePreview,
      isPreviewOpen: () => openRef.current && !collapsedRef.current,
    }),
    [collapsedRef, toggle, openRef, openPreviewRef, closePreview],
  );

  return (
    <Accordion title={t.title} open={!collapsed} onToggle={toggle}>
      <p className={styles.note}>{t.note}</p>
      <div className={styles.reportBody}>
        <fieldset className={styles.version} aria-label={t.versionLabel}>
          <label>
            <input
              type="radio"
              name={radioName}
              value="full"
              checked={version === "full"}
              onChange={() => setVersion("full")}
            />{" "}
            {t.full} <small>{t.fullHint}</small>
          </label>
          <label>
            <input
              type="radio"
              name={radioName}
              value="compact"
              checked={version === "compact"}
              onChange={() => setVersion("compact")}
            />{" "}
            {t.compact} <small>{t.compactHint}</small>
          </label>
        </fieldset>
        <div className={styles.actions}>
          {/* the language menu on opening the preview only: the same button hides it */}
          <ReportLanguageMenu label={t.show} onPick={(language) => void openPreview(language)}>
            {({ open: menuOpen, toggle: toggleMenu }) => (
              <Button
                variant="secondary"
                data-tut="reportPreviewBtn"
                aria-haspopup={open ? undefined : "menu"}
                aria-expanded={open || menuOpen}
                disabled={opening || !source.calculationId}
                onClick={open ? closePreview : toggleMenu}
              >
                {opening ? t.generating : open ? t.hide : t.show}
              </Button>
            )}
          </ReportLanguageMenu>
          <ReportLanguageMenu label={t.generatePdf} onPick={(language) => void print(version, language)}>
            {({ open: menuOpen, toggle }) => (
              <Button
                isLoading={isPrinting}
                disabled={!source.calculationId}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={toggle}
              >
                {t.generatePdf}
              </Button>
            )}
          </ReportLanguageMenu>
          <Button variant="secondary" disabled={!dxf.ok} title={dxf.reason} onClick={doExportDxf}>
            {t.exportDxf}
          </Button>
        </div>
      </div>
      {/* unmounted while closed: in a closed <details> the preview would keep its pages with nothing to show */}
      {open && !collapsed ? (
        previewFailed && !previewUrl ? (
          <p className={styles.preview}>{t.previewError}</p>
        ) : (
          <ReportPreview className={styles.preview} pdfUrl={previewUrl} />
        )
      ) : null}
    </Accordion>
  );
};

export default ReportCard;
